import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, doc, onSnapshot, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAppSelector } from 'src/salon/app/hook';
import { useNavigate } from 'react-router-dom';
import Styles from './styles.module.css';

// ============================================================
// Types
// ============================================================
interface SectionScore {
  label: string;
  score: number;
  max: number;
  issues: string[];
}

interface ScoreResult {
  totalScore: number;
  sections: SectionScore[];
}

interface AiIssue {
  severity: '要注意' | '軽微' | '参考';
  section: string;
  problem: string;
  suggestion: string;
}

interface AiResult {
  issues: AiIssue[];
  overallComment: string;
  strategicAdvice: string[];
}

// ============================================================
// Image quality check
// ============================================================
interface ImgCheck {
  label: string;
  width: number;
  height: number;
  severity: 'ok' | 'warn' | 'poor';
}

const BASE = 'https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o';

function checkImg(url: string): Promise<{ w: number; h: number }> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload  = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = url;
  });
}

function imgSev(w: number, h: number): ImgCheck['severity'] {
  const m = Math.min(w, h);
  if (m >= 1080) return 'ok';
  if (m >= 600)  return 'warn';
  return 'poor';
}

// ============================================================
// Scoring helpers
// ============================================================
const safeStr = (v: any): string => (typeof v === 'string' ? v.trim() : '');
const safeArr = (v: any): any[] => (Array.isArray(v) ? v : []);

function scoreByLen(len: number, tiers: [number, number][]): number {
  let pts = 0;
  for (const [threshold, points] of tiers) {
    if (len >= threshold) pts = points;
    else break;
  }
  return pts;
}

export function calcScore(data: any): ScoreResult {
  const sections: SectionScore[] = [];

  // 1. 企業PR = 15点（タイトル10点 + 特徴5点）
  {
    const title = safeStr(data.summary?.title);
    const pr    = safeStr(data.summary?.companyPr || data.promotion?.companyPr);
    const titleScore = title.length === 0 ? 0 : title.length <= 20 ? 5 : 10;
    const prScore    = scoreByLen(pr.length, [[1, 2], [50, 3], [80, 5]]);
    const issues: string[] = [];
    if (!title) issues.push('タイトルが未入力です（上限30文字）');
    else if (title.length <= 20) issues.push(`タイトルが短めです（${title.length}/30文字）21文字以上推奨`);
    if (!pr) issues.push('特徴が未入力です（上限100文字）');
    else if (pr.length < 80) issues.push(`特徴をもっと充実させましょう（${pr.length}/100文字）80文字以上推奨`);
    sections.push({ label: '企業PR', score: titleScore + prScore, max: 15, issues });
  }

  // 2. トップ画像 = 15点（1〜2枚:5点 / 3〜4枚:10点 / 5枚:15点）
  {
    const images = safeArr(data.summary?.images).filter((id: string) => !!id);
    const count  = Math.min(images.length, 5);
    const score  = count === 0 ? 0 : count <= 2 ? 5 : count <= 4 ? 10 : 15;
    const issues: string[] = [];
    if (count === 0) issues.push('トップ画像が設定されていません（最大5枚）');
    else if (count < 3) issues.push(`トップ画像が少ないです（${count}/5枚）3枚以上推奨`);
    else if (count < 5) issues.push(`トップ画像 ${count}/5枚（5枚で満点）`);
    sections.push({ label: 'トップ画像', score, max: 15, issues });
  }

  // 3. 会社の魅力3選 = 10点（合計文字数で評価）
  {
    const totalLen = [1, 2, 3].reduce((sum, i) =>
      sum + safeStr(data.point?.[`title${i}`]).length, 0);
    const score = scoreByLen(totalLen, [[30, 3], [60, 6], [100, 10]]);
    const issues: string[] = [];
    if (totalLen === 0) issues.push('魅力3選が未入力です（3項目合計）');
    else if (totalLen < 30)  issues.push(`もう少し内容を充実させましょう（合計 ${totalLen}文字）`);
    else if (totalLen < 60)  issues.push(`合計 ${totalLen}文字（60文字以上でさらに加点）`);
    else if (totalLen < 100) issues.push(`合計 ${totalLen}文字（100文字以上で満点）`);
    sections.push({ label: '会社の魅力3選', score, max: 10, issues });
  }

  // 4. 写真で見る = 30点（10点×3セット）
  {
    const issues: string[] = [];
    let total = 0;
    for (let i = 1; i <= 3; i++) {
      const item   = data.strong?.[`item${i}`];
      const title  = safeStr(item?.title);
      const desc   = safeStr(item?.desc);
      const images = safeArr(item?.images);
      const tScore = title ? 2 : 0;
      const dScore = scoreByLen(desc.length, [[1, 1], [50, 3]]);
      const iScore = images.length === 0 ? 0 : images.length === 1 ? 2 : images.length === 2 ? 3 : 5;
      total += tScore + dScore + iScore;
      if (!title)               issues.push(`写真セクション${i}：タイトルを入力してください`);
      if (!desc)                issues.push(`写真セクション${i}：説明文を入力してください`);
      else if (desc.length < 50) issues.push(`写真セクション${i}：説明文 ${desc.length}/100文字（50文字以上推奨）`);
      if (images.length === 0)   issues.push(`写真セクション${i}：画像を設定してください（3枚まで）`);
      else if (images.length < 3) issues.push(`写真セクション${i}：画像 ${images.length}/3枚`);
    }
    sections.push({ label: '写真で見る', score: Math.min(total, 30), max: 30, issues });
  }

  // 5. インタビュー = 15点（2名ペア×3組、各5点）
  {
    const count  = Math.min(Math.max(Number(data.interviews?.count) || 2, 1), 6);
    const issues: string[] = [];
    let total = 0;

    const pairScore = (len: number) => scoreByLen(len, [[50, 1], [100, 3], [160, 5]]);
    const pairLen = (a: number, b: number) =>
      [a, b].filter(i => i <= count).reduce((sum, i) =>
        sum + safeStr(data.interviews?.[`item${i}`]?.desc).length, 0);

    const len12 = pairLen(1, 2);
    total += pairScore(len12);
    if (len12 === 0) issues.push('インタビュー1〜2：内容を入力してください');
    else if (len12 < 160) issues.push(`インタビュー1〜2：合計 ${len12}/160文字（160文字以上で満点）`);

    if (count >= 3) {
      const len34 = pairLen(3, 4);
      total += pairScore(len34);
      if (len34 === 0) issues.push('インタビュー3〜4：内容を入力してください');
      else if (len34 < 160) issues.push(`インタビュー3〜4：合計 ${len34}/160文字`);
    }

    if (count >= 5) {
      const len56 = pairLen(5, 6);
      total += pairScore(len56);
      if (len56 === 0) issues.push('インタビュー5〜6：内容を入力してください');
      else if (len56 < 160) issues.push(`インタビュー5〜6：合計 ${len56}/160文字`);
    }

    // インタビュー人数(count)が少ないと、内容を満点まで埋めても
    // 2名ペア×5点分しか評価対象にならず、満点15点に届かない
    // (例: 2名なら最大5点)。このままだと issues が空のまま
    // 低スコアで「問題ありません」と表示され紛らわしいため、
    // 人数を増やせば点数が伸びることを案内する。
    if (count < 5) {
      const nextCount = count < 3 ? 4 : 6;
      const achievableMax = count < 3 ? 5 : 10;
      issues.push(`インタビュー人数を${nextCount}名にしてみましょう！（現在の人数設定では最大${achievableMax}点までしか獲得できません）`);
    }

    sections.push({ label: 'インタビュー', score: Math.min(total, 15), max: 15, issues });
  }

  // 6. 求める人物像 = 5点（4軸が設定済みなら5点。設定済みの軸の数に応じて加点）
  //    「設定済み」= left + right > 0。0:0 は未設定。
  //    以前は「4軸の左の値が全部同じなら0点」だったため、全部を中央(50)にした求人も未設定扱いになっていた。
  {
    const axes = ['H_C', 'T_E', 'J_P', 'S_R'] as const;
    const setCount = axes.filter(ax => ((data.person?.[ax]?.left ?? 0) + (data.person?.[ax]?.right ?? 0)) > 0).length;
    const score = Math.round((5 * setCount) / axes.length);
    const issues: string[] = [];
    if (setCount === 0) issues.push('求める人物像が設定されていません');
    else if (setCount < axes.length) issues.push(`求める人物像に未設定の項目があります（設定済み ${setCount}/${axes.length}）`);
    sections.push({ label: '求める人物像', score, max: 5, issues });
  }

  // 7. 雇用条件・応募情報 = 10点（公開中は固定）
  {
    sections.push({ label: '雇用条件・応募情報', score: 10, max: 10, issues: [] });
  }

  const totalScore = sections.reduce((sum, s) => sum + s.score, 0);
  return { totalScore, sections };
}

// ============================================================
// AI advice
// ============================================================
function buildAdvicePrompt(data: any, result: ScoreResult): string {
  const title = safeStr(data.summary?.title);
  const pr    = safeStr(data.summary?.companyPr || data.promotion?.companyPr);
  const ivCount = Math.min(Math.max(Number(data.interviews?.count) || 2, 1), 6);

  const fillInfo = [
    `タイトル: ${title.length}/30文字`,
    `特徴: ${pr.length}/100文字`,
    ...[1, 2, 3].map(i => {
      const t = safeStr(data.point?.[`title${i}`]);
      return `魅力${i}: ${t.length}/40文字`;
    }),
    ...[1, 2, 3].map(i => {
      const desc = safeStr(data.strong?.[`item${i}`]?.desc);
      return `写真${i}説明: ${desc.length}/100文字`;
    }),
    ...[1, 2, 3, 4, 5, 6].slice(0, ivCount).map(i => {
      const desc = safeStr(data.interviews?.[`item${i}`]?.desc);
      return `インタビュー${i}: ${desc.length}/100文字`;
    }),
  ];

  const scoreInfo = result.sections.map(s => `${s.label}: ${s.score}/${s.max}点`).join(' / ');

  const lines = [
    `サロン名: ${safeStr(data.summary?.name || data.info?.companyName)}`,
    `タイトル: ${title}`,
    `特徴: ${pr}`,
    `\n【会社の魅力3選】`,
    ...[1, 2, 3].map(i => `  魅力${i}: ${safeStr(data.point?.[`title${i}`])}`),
    `\n【写真で見る】`,
    ...[1, 2, 3].map(i => {
      const item = data.strong?.[`item${i}`];
      return `  写真${i} タイトル: ${safeStr(item?.title)} / 説明: ${safeStr(item?.desc)}`;
    }),
    `\n【インタビュー（${ivCount}名）】`,
    ...[1, 2, 3, 4, 5, 6].slice(0, ivCount).map(i => {
      const iv = data.interviews?.[`item${i}`];
      return `  ${i}: ${safeStr(iv?.name)}（${safeStr(iv?.title)}）: ${safeStr(iv?.desc)}`;
    }),
  ];

  return `あなたは美容業界サロンの新卒採用専門の求人改善アドバイザーです。
以下の求人情報について分析してください。

【現在のスコア】
総合: ${result.totalScore}/100点
${scoreInfo}

【各フィールドの文字数（現在/上限）】
${fillInfo.join('\n')}

【求人内容】
${lines.join('\n')}

---

## Step 1：方向性チェック（最優先）
まず全体を読んで、「学生（求職者）に向けた採用訴求」になっているかを確認してください。
- 集客・リピート・売上を高めるための内容（お客様向け）になっていないか
- 「学生がこのサロンで働くとどうなるか」という視点で書かれているか
- 方向性がずれている箇所は severity「要注意」で指摘し、採用訴求に書き直した例文を示す

## Step 2：フィールドごとの品質チェック
以下の観点でチェックしてください：
1. 抽象的・ありきたりな表現（「アットホーム」「成長できる」など、どのサロンでも言えること）
2. 内容の重複（複数セクションで同じことを言っていないか）
3. 文字数が上限の80%未満の箇所（伸び代あり）
4. 数字・制度・具体的エピソードで裏付けられているか

## Step 3：全体戦略提案
「このサロンの求人をもっと良くするために追加すると効果的な情報・切り口」を3〜4件、提案してください。
例：「入社後の1日スケジュールを具体的に記載する」「先輩スタッフの離職率・定着率の数字を入れる」など
採用文脈で学生が知りたい情報に絞り、このサロン特有の強みを活かした提案にすること。

---

【severity の使い分け】
- 「要注意」: 採用訴求の方向性がずれている、または内容の質に大きく影響する問題
- 「軽微」: 改善できればより良いが優先度は低い
- 「参考」: すでに高品質だが磨ける余地がある提案
- 文字数が上限の90%以上のフィールドには「もっと書いて」と言わず、内容の質のみを指摘する
- 総合スコア80点以上は高品質と認識し、genuine な問題以外は「参考」扱いにする

【アドバイスの書き方ルール】
- suggestion は漠然とした指示で終わらせない
- 必ず「例えば：『〇〇』のような表現はいかがでしょうか」という形で実際に使える文章例を1〜2文提示する
- 美容業界・新卒採用の文脈に沿った自然な日本語で書く

以下のJSON形式のみで回答（説明文・コードブロック不要）:
{
  "issues": [
    { "severity": "要注意", "section": "セクション名", "problem": "問題の説明", "suggestion": "具体的な改善提案（例文を必ず含める）" }
  ],
  "overallComment": "全体的な一言コメント（50字以内）",
  "strategicAdvice": [
    "このサロンに効果的な全体戦略提案1",
    "全体戦略提案2",
    "全体戦略提案3"
  ]
}`;
}

const DAILY_LIMIT = 3;

async function callGemini(prompt: string): Promise<AiResult> {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  if (!apiKey) throw new Error('Gemini APIキーが設定されていません');
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
    }
  );
  if (!res.ok) throw new Error(`Gemini API error: ${res.status}`);
  const json = await res.json();
  const text = json.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
  return JSON.parse(text.replace(/```json\n?|\n?```/g, '').trim()) as AiResult;
}

// ============================================================
// UI helpers
// ============================================================
function scoreColor(pct: number): string {
  if (pct >= 80) return '#4CAF50';
  if (pct >= 40) return '#EAC630';
  return '#E44E70';
}

export function ScoreCircle({ score, max }: { score: number; max: number }) {
  const pct   = max > 0 ? Math.round((score / max) * 100) : 0;
  const r     = 52;
  const circ  = 2 * Math.PI * r;
  const dash  = (pct / 100) * circ;
  const color = scoreColor(pct);

  return (
    <div className={Styles.circle_wrap}>
      <svg viewBox="0 0 120 120" width="120" height="120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#F0F0F0" strokeWidth="12" />
        <circle
          cx="60" cy="60" r={r} fill="none"
          stroke={color} strokeWidth="12"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <div className={Styles.circle_inner}>
        <span className={Styles.circle_score} style={{ color }}>{score}</span>
        <span className={Styles.circle_max}>/100</span>
      </div>
    </div>
  );
}

function ScoreBar({ score, max }: { score: number; max: number }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className={Styles.bar_track}>
      <div
        className={Styles.bar_fill}
        style={{ width: `${pct}%`, background: scoreColor(pct) }}
      />
    </div>
  );
}

// ============================================================
// Main component
// ============================================================
function sectionToNav(section: string): number | null {
  if (/企業PR|タイトル|特徴/.test(section)) return 1;
  if (/トップ画像/.test(section))           return 1;
  if (/魅力/.test(section))                 return 3;
  if (/数字/.test(section))                 return 4;
  if (/写真/.test(section))                 return 5;
  if (/インタビュー/.test(section))         return 6;
  if (/求める人物像/.test(section))         return 7;
  if (/雇用|応募/.test(section))            return 8;
  return null;
}

const ScorePage: React.FC = () => {
  const company  = useAppSelector((state) => state.company.company);
  const navigate = useNavigate();
  const [data,   setData]   = useState<any>(null);
  const [result, setResult] = useState<ScoreResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [published, setPublished] = useState(false);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult,  setAiResult]  = useState<AiResult | null>(null);
  const [aiError,   setAiError]   = useState('');
  const aiInitRef = useRef(false);

  const [staffCount,    setStaffCount]    = useState<number | null>(null);
  const [graduateCount, setGraduateCount] = useState<number | null>(null);
  const [couponCount,   setCouponCount]   = useState<number | null>(null);

  const [imageChecks,  setImageChecks]  = useState<ImgCheck[]>([]);
  const [imgChecking,  setImgChecking]  = useState(false);

  useEffect(() => {
    if (!company?.id) return;
    const db  = getFirestore();
    const ref = doc(db, 'recruits', company.id + '_2027');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setData(d);
        setResult(calcScore(d));
        setPublished(d.recruitStatus === 1);
        if (!aiInitRef.current && d.aiAdviceResult) {
          setAiResult(d.aiAdviceResult as AiResult);
          aiInitRef.current = true;
        }
      }
      setLoading(false);
    });
    return () => unsub();
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) return;
    const db = getFirestore();
    const staffQ  = query(collection(db, 'staffProfiles'), where('companyId', '==', company.id));
    const gradQ   = query(collection(db, 'graduation'),    where('companyId', '==', company.id));
    const couponQ = query(collection(db, 'coupons'),       where('companyId', '==', company.id));
    Promise.all([getDocs(staffQ), getDocs(gradQ), getDocs(couponQ)]).then(([s, g, c]) => {
      setStaffCount(s.size);
      setGraduateCount(g.size);
      setCouponCount(c.size);
    });
  }, [company?.id]);

  useEffect(() => {
    if (!data || !company?.id) return;
    setImgChecking(true);

    const tasks: { label: string; url: string }[] = [];

    safeArr(data.summary?.images).filter(Boolean).forEach((id: string, i: number) => {
      tasks.push({ label: `トップ画像 ${i + 1}`, url: `${BASE}/styles%2F${id}?alt=media` });
    });

    for (let n = 1; n <= 3; n++) {
      safeArr(data.strong?.[`item${n}`]?.images).filter(Boolean).forEach((id: string, i: number) => {
        tasks.push({ label: `写真セクション${n} 画像${i + 1}`, url: `${BASE}/recruitImage${n}%2F${id}?alt=media` });
      });
    }

    const ivCount = Math.min(Math.max(Number(data.interviews?.count) || 2, 1), 6);
    for (let i = 1; i <= ivCount; i++) {
      const imgId = safeStr(data.interviews?.[`item${i}`]?.image);
      if (imgId) tasks.push({ label: `インタビュー${i}`, url: `${BASE}/interview%2F${imgId}?alt=media` });
    }

    const db = getFirestore();
    const storeQ = query(collection(db, 'stores'), where('companyId', '==', company.id));
    getDocs(storeQ).then(snap => {
      snap.docs.forEach((d, i) => {
        const imgId = d.data().image;
        if (imgId) tasks.push({ label: `店舗 ${i + 1}`, url: `${BASE}/stores%2F${imgId}?alt=media` });
      });

      return Promise.all(
        tasks.map(async t => {
          const { w, h } = await checkImg(t.url);
          return { label: t.label, width: w, height: h, severity: imgSev(w, h) } as ImgCheck;
        })
      );
    }).then(results => {
      setImageChecks(results.filter(r => r.width > 0));
      setImgChecking(false);
    });
  }, [data, company?.id]);

  const todayJST  = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
  const usedToday = data?.aiAdviceUsage?.date === todayJST ? (data?.aiAdviceUsage?.count ?? 0) : 0;
  const remaining = DAILY_LIMIT - usedToday;

  const handleAiAdvice = async () => {
    if (!data || !result || !company?.id) return;
    setAiLoading(true);
    setAiError('');
    try {
      const prompt = buildAdvicePrompt(data, result);
      const res    = await callGemini(prompt);
      const db     = getFirestore();
      await updateDoc(doc(db, 'recruits', `${company.id}_2027`), {
        aiAdviceUsage:  { date: todayJST, count: usedToday + 1 },
        aiAdviceResult: res,
      });
      setAiResult(res);
    } catch (e: any) {
      setAiError(e?.message ?? 'エラーが発生しました');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return <div className={Styles.loading}>読み込み中...</div>;
  }

  if (!result) {
    return <div className={Styles.empty}>求人データが見つかりませんでした。</div>;
  }

  const gradeLabel = (score: number) => {
    if (score >= 80) return { text: '良好', color: '#4CAF50' };
    if (score >= 40) return { text: '改善推奨', color: '#EAC630' };
    return { text: '要改善', color: '#E44E70' };
  };
  const grade = gradeLabel(result.totalScore);

  return (
    <div className={Styles.container}>

      {/* 総合スコアカード */}
      <div className={Styles.total_card}>
        <ScoreCircle score={result.totalScore} max={100} />
        <div className={Styles.total_info}>
          <div className={Styles.total_grade} style={{ color: grade.color }}>
            {grade.text}
          </div>
          <h2 className={Styles.total_title}>求人クオリティスコア</h2>
          <p className={Styles.total_desc}>
            {!published && (
              <span className={Styles.unpublished_badge}>未公開</span>
            )}
            各セクションを充実させると求職者への訴求力が上がります。
          </p>
        </div>
      </div>

      {/* セクション別スコア */}
      <div className={Styles.sections_grid}>
        {result.sections.map(s => {
          const pct = Math.round((s.score / s.max) * 100);
          return (
            <div key={s.label} className={Styles.section_card}>
              <div className={Styles.section_header}>
                <span className={Styles.section_name}>{s.label}</span>
                <span className={Styles.section_score} style={{ color: scoreColor(pct) }}>
                  {s.score}<span className={Styles.section_max}>/{s.max}点</span>
                </span>
              </div>
              <ScoreBar score={s.score} max={s.max} />
              {s.issues.length > 0 ? (
                <ul className={Styles.issues}>
                  {s.issues.map((iss, idx) => (
                    <li key={idx} className={Styles.issue_item}>{iss}</li>
                  ))}
                </ul>
              ) : (
                <p className={Styles.no_issues}>問題ありません</p>
              )}
            </div>
          );
        })}
      </div>

      {/* 画像品質チェック */}
      <div className={Styles.extra_card}>
        <h3 className={Styles.extra_title}>画像品質チェック</h3>
        <p className={Styles.extra_desc}>元画像のサイズを確認します（推奨：1080×1080px以上）</p>
        {imgChecking ? (
          <p className={Styles.img_checking}>チェック中...</p>
        ) : imageChecks.length === 0 ? (
          <p className={Styles.no_issues}>画像が登録されていません</p>
        ) : (
          <>
            <div className={Styles.img_summary}>
              {(['ok', 'warn', 'poor'] as const).map(sev => {
                const count = imageChecks.filter(c => c.severity === sev).length;
                const label = sev === 'ok' ? '良好' : sev === 'warn' ? '注意' : '要改善';
                const color = sev === 'ok' ? '#4CAF50' : sev === 'warn' ? '#EAC630' : '#E44E70';
                return (
                  <div key={sev} className={Styles.img_summary_item}>
                    <span className={Styles.img_summary_badge} style={{ background: color }}>{label}</span>
                    <span className={Styles.img_summary_count}>{count}枚</span>
                  </div>
                );
              })}
            </div>
            {imageChecks.filter(c => c.severity !== 'ok').length > 0 && (
              <div className={Styles.img_issue_list}>
                {imageChecks.filter(c => c.severity !== 'ok').map((c, i) => (
                  <div key={i} className={`${Styles.img_issue_row} ${c.severity === 'poor' ? Styles.img_poor : Styles.img_warn}`}>
                    <span className={Styles.img_issue_label}>{c.label}</span>
                    <span className={Styles.img_issue_dim}>{c.width} × {c.height}px</span>
                    <span className={Styles.img_issue_badge}>{c.severity === 'poor' ? '要改善' : '注意'}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* その他タブの登録状況 */}
      <div className={Styles.extra_card}>
        <h3 className={Styles.extra_title}>その他の登録状況</h3>
        <p className={Styles.extra_desc}>以下の情報を充実させると、学生への周知力にブーストがかかります。</p>
        <div className={Styles.extra_grid}>
          <div className={Styles.extra_item}>
            <span className={Styles.extra_label}>スタッフ紹介</span>
            <span className={Styles.extra_count}>{staffCount ?? '—'}<span className={Styles.extra_unit}>件</span></span>
            <span className={Styles.extra_hint}>もっと登録するとアルゴリズムに反映されます</span>
          </div>
          <div className={Styles.extra_item}>
            <span className={Styles.extra_label}>卒業生</span>
            <span className={Styles.extra_count}>{graduateCount ?? '—'}<span className={Styles.extra_unit}>件</span></span>
            <span className={Styles.extra_hint}>もっと登録するとアルゴリズムに反映されます</span>
          </div>
          <div className={Styles.extra_item}>
            <span className={Styles.extra_label}>クーポン</span>
            <span className={Styles.extra_count}>{couponCount ?? '—'}<span className={Styles.extra_unit}>件</span></span>
            <span className={Styles.extra_hint}>もっと登録するとアルゴリズムに反映されます</span>
          </div>
        </div>
      </div>

      {/* AIアドバイス */}
      <div className={Styles.ai_card}>
        <div className={Styles.ai_header}>
          <div>
            <h3 className={Styles.ai_title}>AIアドバイス</h3>
            <p className={Styles.ai_desc}>求人内容を分析して改善提案を生成します<br />※AIは求人内容を確認しアドバイスを行うため、不要なアドバイスであれば無視してください。</p>
          </div>
          <button
            className={Styles.ai_btn}
            onClick={handleAiAdvice}
            disabled={aiLoading || remaining <= 0}
          >
            {aiLoading ? '分析中...' : remaining > 0 ? `アドバイスを生成（残り${remaining}回）` : '本日の上限に達しました'}
          </button>
        </div>

        {aiError && (
          <p className={Styles.ai_error}>{aiError}</p>
        )}

        {aiResult && (
          <div className={Styles.ai_result}>
            <p className={Styles.ai_comment}>💬 {aiResult.overallComment}</p>
            <div className={Styles.ai_issues}>
              {aiResult.issues.map((issue, idx) => {
                const navIndex = sectionToNav(issue.section);
                return (
                  <div key={idx} className={`${Styles.ai_issue_item} ${issue.severity === '要注意' ? Styles.ai_critical : issue.severity === '軽微' ? Styles.ai_minor : Styles.ai_hint}`}>
                    <div className={Styles.ai_issue_header}>
                      <span className={Styles.ai_severity}>{issue.severity}</span>
                      <span className={Styles.ai_section_tag}>{issue.section}</span>
                      {navIndex !== null && (
                        <button
                          className={Styles.ai_goto_btn}
                          onClick={() => navigate('/service/recruit', { state: { recruitSection: navIndex } })}
                        >
                          編集する →
                        </button>
                      )}
                    </div>
                    <p className={Styles.ai_problem}>{issue.problem}</p>
                    <p className={Styles.ai_suggestion}>→ {issue.suggestion}</p>
                  </div>
                );
              })}
            </div>

            {aiResult.strategicAdvice?.length > 0 && (
              <div className={Styles.strategic_block}>
                <p className={Styles.strategic_title}>全体戦略の提案</p>
                <ul className={Styles.strategic_list}>
                  {aiResult.strategicAdvice.map((advice, idx) => (
                    <li key={idx} className={Styles.strategic_item}>{advice}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default ScorePage;
